import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Shield, Save, Trash2, AlertTriangle, Search, Database, Download, Upload, Clock, Wrench, RotateCcw, Calculator } from "lucide-react";
import DriveBackupCard from "@/components/DriveBackupCard";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function MigrateServicesAction() {
  const [running, setRunning] = useState(false);

  const run = async () => {
    if (!window.confirm("Move products with categories Training / Calibration / Rental / Repair into the Services tab?\n\nThis is idempotent — safe to run multiple times.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/migrate-services-from-products");
      if (data.moved === 0) {
        toast.success("No products needed migration — Services tab is already clean.");
      } else {
        toast.success(`Moved ${data.moved} item${data.moved === 1 ? "" : "s"} into Services`);
      }
    } catch (err) { toast.error(fmtError(err)); } finally { setRunning(false); }
  };

  return (
    <Button
      variant="outline"
      onClick={run}
      disabled={running}
      className="rounded-2xl tap"
      data-testid="migrate-services-button"
    >
      <Wrench className="h-4 w-4 mr-1.5" /> {running ? "Migrating…" : "Run migration"}
    </Button>
  );
}

function WipeContactsAction() {
  const [preview, setPreview] = useState(null); // { count }
  const [scanning, setScanning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/wipe-contacts?dry_run=true");
      setPreview(data);
      setTyped("");
      setOpen(true);
    } catch (err) { toast.error(fmtError(err)); } finally { setScanning(false); }
  };

  const confirm = async () => {
    if (typed !== "WIPE") return;
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/wipe-contacts?confirm=WIPE");
      const n = data.deleted || 0;
      toast.success(n ? `Removed ${n} customer contact${n === 1 ? "" : "s"}` : "Contacts already empty");
      setOpen(false);
      setPreview(null);
      setTyped("");
    } catch (err) { toast.error(fmtError(err)); } finally { setConfirming(false); }
  };

  const total = preview?.count || 0;

  return (
    <>
      <Button
        variant="destructive"
        onClick={scan}
        disabled={scanning}
        className="rounded-2xl tap"
        data-testid="wipe-contacts-button"
      >
        <Trash2 className="h-4 w-4 mr-1.5" /> {scanning ? "Scanning…" : "Wipe customer contacts"}
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!confirming) setOpen(v); if (!v) setTyped(""); }}>
        <AlertDialogContent className="rounded-3xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Wipe customer contacts
            </AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deletes <strong>every customer contact / decision-maker</strong> in the database. Clients, Leads, Marketing Contacts, Deals and Quotations are <strong>preserved</strong>.
              <br /><br />
              <strong className="text-red-600">This cannot be undone.</strong> Each rep will need to re-add their contacts after this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {total === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No customer contacts to delete.</p>
          ) : (
            <div className="my-2 rounded-2xl border border-border/60 p-4 bg-muted/30 text-center">
              <p className="text-3xl font-extrabold text-red-700">{total}</p>
              <p className="text-xs text-muted-foreground mt-1">customer contact{total === 1 ? "" : "s"} will be deleted</p>
            </div>
          )}
          {total > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Type <code className="px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 font-mono">WIPE</code> to confirm:</Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="rounded-2xl h-11 font-mono"
                placeholder="WIPE"
                autoFocus
                data-testid="wipe-contacts-typed-confirm"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            {total > 0 && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirm(); }}
                disabled={typed !== "WIPE" || confirming}
                className="rounded-2xl bg-red-600 hover:bg-red-700"
                data-testid="wipe-contacts-confirm"
              >
                {confirming ? "Deleting…" : `Delete ${total}`}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RecomputeAllPointsAction() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const preview = await api.post("/admin/performance/recompute-all-points?dry_run=true");
      const { total_events, changed, sum_old_awarded, sum_new_awarded, delta } = preview.data;
      if (changed === 0) { toast.success("Already up to date — no rows need recomputing"); return; }
      const sign = delta >= 0 ? "+" : "";
      const ok = window.confirm(
        `Recompute every point event with the current rates?\n\n` +
        `• Rows to change: ${changed} / ${total_events}\n` +
        `• Old total awarded: ${sum_old_awarded}\n` +
        `• New total awarded: ${sum_new_awarded} (${sign}${delta})\n\n` +
        `Audit history (timestamps, source links) is preserved. Continue?`,
      );
      if (!ok) return;
      const res = await api.post("/admin/performance/recompute-all-points?dry_run=false");
      toast.success(`Recomputed ${res.data.written} events. Net delta ${res.data.delta >= 0 ? "+" : ""}${res.data.delta} pts.`);
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  return (
    <Button
      variant="outline"
      onClick={run}
      disabled={busy}
      className="rounded-2xl tap"
      data-testid="recompute-all-points-button"
    >
      <Calculator className="h-4 w-4 mr-1.5" /> {busy ? "Working…" : "Recompute all points"}
    </Button>
  );
}

function CleanupPhantomEventsAction() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const preview = await api.post("/admin/performance/cleanup-phantom-events?dry_run=true");
      const n = preview.data.phantom_count || 0;
      if (n === 0) { toast.success("No phantom events found — leaderboard already clean"); return; }
      const reasons = Object.entries(preview.data.by_reason || {}).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ");
      const ok = window.confirm(`Wipe ${n} phantom point events?\n\nBreakdown: ${reasons}\n\nThis CANNOT be undone, but business data is untouched.`);
      if (!ok) return;
      const res = await api.post("/admin/performance/cleanup-phantom-events?dry_run=false");
      toast.success(`Removed ${res.data.deleted} phantom events. Leaderboard will reconcile on next refresh.`);
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  return (
    <Button
      variant="outline"
      onClick={run}
      disabled={busy}
      className="rounded-2xl tap"
      data-testid="cleanup-phantom-events-button"
    >
      <Search className="h-4 w-4 mr-1.5" /> {busy ? "Scanning…" : "Cleanup phantom events"}
    </Button>
  );
}

function ResetPerfHubAction() {
  const [preview, setPreview] = useState(null); // dry-run response
  const [scanning, setScanning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/performance/reset", { dry_run: true, drop_demo_seeds: true });
      setPreview(data);
      setTyped("");
      setOpen(true);
    } catch (err) { toast.error(fmtError(err)); } finally { setScanning(false); }
  };

  const confirm = async () => {
    if (typed !== "RESET") return;
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/performance/reset", { dry_run: false, confirm: true, drop_demo_seeds: true });
      const total = (data.history_total_records || 0) + (data.demo_seeds_removed || 0);
      toast.success(total ? `Reset complete — ${data.history_total_records} history records cleared` : "Already empty");
      setOpen(false);
      setPreview(null);
      setTyped("");
    } catch (err) { toast.error(fmtError(err)); } finally { setConfirming(false); }
  };

  const total = preview?.history_total_records || 0;
  const byCollection = preview?.history_by_collection || {};
  const demoEmails = preview?.demo_seed_emails || [];
  const visibleCounts = Object.entries(byCollection).filter(([, v]) => v > 0);

  return (
    <>
      <Button
        variant="destructive"
        onClick={scan}
        disabled={scanning}
        className="rounded-2xl tap"
        data-testid="reset-perf-hub-button"
      >
        <RotateCcw className="h-4 w-4 mr-1.5" /> {scanning ? "Scanning…" : "Reset Performance Hub"}
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!confirming) setOpen(v); if (!v) setTyped(""); }}>
        <AlertDialogContent className="rounded-3xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Reset Performance Hub
            </AlertDialogTitle>
            <AlertDialogDescription>
              Clears every point, milestone, streak and notification in Performance Hub so all reps start from <strong>zero</strong>. Business data (deals, leads, clients, quotations, activities) is <strong>preserved</strong>.
              <br /><br />
              <strong className="text-red-600">This cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {total === 0 && demoEmails.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Performance Hub is already empty.</p>
          ) : (
            <div className="my-2 max-h-56 overflow-y-auto rounded-2xl border border-border/60 p-3 space-y-1.5 bg-muted/30">
              <p className="text-xs font-semibold mb-1">{total} record{total === 1 ? "" : "s"} will be cleared:</p>
              {visibleCounts.map(([col, v]) => (
                <div key={col} className="flex items-center justify-between text-xs">
                  <span className="capitalize">{col.replace(/_/g, " ")}</span>
                  <span className="font-bold text-red-700">{v}</span>
                </div>
              ))}
              {demoEmails.length > 0 && (
                <div className="pt-2 mt-2 border-t border-border/60">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Demo seed users (will be removed)</p>
                  {demoEmails.map((e) => (
                    <div key={e} className="text-[10px] text-muted-foreground truncate">{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {(total > 0 || demoEmails.length > 0) && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Type <code className="px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 font-mono">RESET</code> to confirm:</Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="rounded-2xl h-11 font-mono"
                placeholder="RESET"
                autoFocus
                data-testid="reset-perf-hub-typed-confirm"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            {(total > 0 || demoEmails.length > 0) && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirm(); }}
                disabled={typed !== "RESET" || confirming}
                className="rounded-2xl bg-red-600 hover:bg-red-700"
                data-testid="reset-perf-hub-confirm"
              >
                {confirming ? "Resetting…" : "Reset"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function WipeAllDataAction() {
  const [preview, setPreview] = useState(null); // { counts, total }
  const [scanning, setScanning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/wipe-all-data?dry_run=true");
      setPreview(data);
      setTyped("");
      setOpen(true);
    } catch (err) { toast.error(fmtError(err)); } finally { setScanning(false); }
  };

  const confirm = async () => {
    if (typed !== "WIPE") return;
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/wipe-all-data?confirm=WIPE");
      const total = data.total || 0;
      toast.success(total ? `Wiped ${total} record${total === 1 ? "" : "s"}` : "Already empty");
      setOpen(false);
      setPreview(null);
      setTyped("");
    } catch (err) { toast.error(fmtError(err)); } finally { setConfirming(false); }
  };

  const total = preview?.total || 0;
  const counts = preview?.counts || {};
  const visibleCounts = Object.entries(counts).filter(([, v]) => v > 0);

  return (
    <>
      <Button
        variant="destructive"
        onClick={scan}
        disabled={scanning}
        className="rounded-2xl tap"
        data-testid="wipe-all-data-button"
      >
        <Trash2 className="h-4 w-4 mr-1.5" /> {scanning ? "Scanning…" : "Wipe ALL CRM data"}
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!confirming) setOpen(v); if (!v) setTyped(""); }}>
        <AlertDialogContent className="rounded-3xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Wipe ALL CRM data
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>every</strong> client, deal, follow-up, lead, quotation, contact, activity, meeting, notification and external calendar event in the database. <strong className="text-red-600">This cannot be undone.</strong>
              <br /><br />
              <span className="text-foreground">Preserved:</span> users, settings, products, services, solutions, audit logs, login history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {total === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Database is already empty.</p>
          ) : (
            <div className="my-2 max-h-56 overflow-y-auto rounded-2xl border border-border/60 p-3 space-y-1.5 bg-muted/30">
              <p className="text-xs font-semibold mb-1">{total} record{total === 1 ? "" : "s"} will be deleted:</p>
              {visibleCounts.map(([col, v]) => (
                <div key={col} className="flex items-center justify-between text-xs">
                  <span className="capitalize">{col.replace(/_/g, " ")}</span>
                  <span className="font-bold text-red-700">{v}</span>
                </div>
              ))}
            </div>
          )}
          {total > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Type <code className="px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 font-mono">WIPE</code> to confirm:</Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="rounded-2xl h-11 font-mono"
                placeholder="WIPE"
                autoFocus
                data-testid="wipe-all-typed-confirm"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            {total > 0 && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirm(); }}
                disabled={typed !== "WIPE" || confirming}
                className="rounded-2xl bg-red-600 hover:bg-red-700"
                data-testid="wipe-all-confirm"
              >
                {confirming ? "Wiping…" : `Delete ${total}`}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function BackupAction() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapping, setSnapping] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreTyped, setRestoreTyped] = useState("");
  const [restoring, setRestoring] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/backup/list"); setBackups(data.backups || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const downloadFresh = async () => {
    try {
      const r = await api.get("/admin/backup/snapshot", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `renviro-snapshot-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded · save it to Google Drive / iCloud");
    } catch (err) { toast.error(fmtError(err)); }
  };

  const downloadArchive = async (filename) => {
    try {
      const r = await api.get(`/admin/backup/download/${encodeURIComponent(filename)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const manualSnap = async () => {
    setSnapping(true);
    try {
      const { data } = await api.post("/admin/backup/manual-snapshot");
      toast.success(`Saved ${data.filename} · ${data.manifest?.total_records || 0} records`);
      refresh();
    } catch (err) { toast.error(fmtError(err)); } finally { setSnapping(false); }
  };

  const onRestoreFilePicked = async (file) => {
    if (!file) return;
    setRestoreFile(file);
    setRestoreTyped("");
    setRestoring(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/backup/restore?dry_run=true", fd);
      setRestorePreview(data);
      setRestoreOpen(true);
    } catch (err) {
      toast.error(fmtError(err));
      setRestoreFile(null);
    } finally { setRestoring(false); }
  };

  const confirmRestore = async () => {
    if (!restoreFile || restoreTyped !== "RESTORE") return;
    setRestoring(true);
    try {
      const fd = new FormData(); fd.append("file", restoreFile);
      const { data } = await api.post("/admin/backup/restore?confirm=RESTORE", fd);
      toast.success(`Restored ${data.total} records · refresh page to see changes`);
      setRestoreOpen(false);
      setRestoreFile(null);
      setRestorePreview(null);
      refresh();
    } catch (err) { toast.error(fmtError(err)); } finally { setRestoring(false); }
  };

  const fmtSize = (b) => b > 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${(b / 1024).toFixed(1)} KB`;
  const fmtDate = (s) => new Date(s).toLocaleString();

  return (
    <>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button onClick={downloadFresh} className="rounded-2xl tap h-12" data-testid="backup-download-fresh">
            <Download className="h-4 w-4 mr-2" /> Download backup now
          </Button>
          <Button variant="outline" onClick={manualSnap} disabled={snapping} className="rounded-2xl tap h-12" data-testid="backup-manual-snap">
            <Clock className="h-4 w-4 mr-2" /> {snapping ? "Saving…" : "Save snapshot to disk"}
          </Button>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Daily auto-snapshots (last 30 days)</Label>
          {loading ? (
            <Skeleton className="h-16 mt-2 rounded-2xl" />
          ) : backups.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-2 italic">No automatic backups yet — first one runs at 02:00 UTC tonight, or click <strong>Save snapshot to disk</strong> above.</p>
          ) : (
            <div className="space-y-1.5 mt-2 max-h-48 overflow-y-auto">
              {backups.map((b) => (
                <button
                  key={b.filename}
                  type="button"
                  onClick={() => downloadArchive(b.filename)}
                  className="w-full flex items-center justify-between p-2.5 rounded-xl border border-border/60 hover:bg-muted/40 transition tap text-left"
                  data-testid={`backup-archive-${b.filename}`}
                >
                  <div>
                    <p className="text-xs font-mono truncate">{b.filename}</p>
                    <p className="text-[10px] text-muted-foreground">{fmtDate(b.taken_at)} · {fmtSize(b.size)}</p>
                  </div>
                  <Download className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-border/40">
          <Label className="text-xs font-semibold inline-flex items-center gap-1.5 text-amber-700"><Upload className="h-3.5 w-3.5" /> Restore from snapshot</Label>
          <input
            id="backup-restore-file"
            type="file"
            accept=".json,application/json"
            onChange={(e) => onRestoreFilePicked(e.target.files?.[0] || null)}
            className="block mt-1 text-xs file:mr-3 file:rounded-xl file:border-0 file:bg-amber-100 file:px-3 file:py-2 file:text-amber-800 file:font-semibold hover:file:bg-amber-200"
            data-testid="backup-restore-file"
          />
          <p className="text-[10px] text-muted-foreground mt-1">Wipes targeted collections and reinserts from the file. Your admin account is preserved so you don't get logged out.</p>
        </div>
      </div>

      <AlertDialog open={restoreOpen} onOpenChange={(v) => { if (!restoring) setRestoreOpen(v); if (!v) { setRestoreFile(null); setRestoreTyped(""); } }}>
        <AlertDialogContent className="rounded-3xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Restore from snapshot
            </AlertDialogTitle>
            <AlertDialogDescription>
              Snapshot taken: <strong>{restorePreview?.manifest?.taken_at ? new Date(restorePreview.manifest.taken_at).toLocaleString() : "—"}</strong>
              <br /><br />
              This will <strong>wipe and replace</strong> every CRM collection with the data inside this file. <strong className="text-amber-700">Cannot be undone.</strong> Download a fresh backup first if you're unsure.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {restorePreview && (
            <div className="my-2 max-h-56 overflow-y-auto rounded-2xl border border-border/60 p-3 space-y-1 bg-muted/30">
              <p className="text-xs font-semibold mb-1">{restorePreview.total} records will be restored:</p>
              {Object.entries(restorePreview.counts || {}).filter(([, v]) => v > 0).map(([col, v]) => (
                <div key={col} className="flex items-center justify-between text-xs">
                  <span className="capitalize">{col.replace(/_/g, " ")}</span>
                  <span className="font-bold">{v}</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Type <code className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 font-mono">RESTORE</code> to confirm:</Label>
            <Input
              value={restoreTyped}
              onChange={(e) => setRestoreTyped(e.target.value)}
              className="rounded-2xl h-11 font-mono"
              placeholder="RESTORE"
              autoFocus
              data-testid="backup-restore-typed"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRestore(); }}
              disabled={restoreTyped !== "RESTORE" || restoring}
              className="rounded-2xl bg-amber-600 hover:bg-amber-700"
              data-testid="backup-restore-confirm"
            >
              {restoring ? "Restoring…" : "Restore now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CleanupTestDataAction() {
  const [preview, setPreview] = useState(null); // { matched: {col: count}, samples: {col: [...]}, total }
  const [scanning, setScanning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/wipe-data?dry_run=true");
      setPreview(data);
      setOpen(true);
    } catch (err) { toast.error(fmtError(err)); } finally { setScanning(false); }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/wipe-data");
      const total = data.total || 0;
      toast.success(total ? `Deleted ${total} test record${total === 1 ? "" : "s"}` : "Already clean — no test data found");
      setOpen(false);
      setPreview(null);
    } catch (err) { toast.error(fmtError(err)); } finally { setConfirming(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={scan}
        disabled={scanning}
        className="rounded-2xl tap border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
        data-testid="cleanup-data-button"
      >
        <Search className="h-4 w-4 mr-1.5" /> {scanning ? "Scanning…" : "Scan for test data"}
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-3xl max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-extrabold">
              {preview?.total ? `Found ${preview.total} test record${preview.total === 1 ? "" : "s"}` : "No test data found"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {preview?.total
                ? "Only records below will be permanently deleted. Real customer data is not in this list."
                : "Nothing matches the test patterns. Your data looks clean."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {preview?.total > 0 && (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto -mx-1 px-1">
              {Object.entries(preview.matched || {}).map(([col, n]) => (
                <div key={col} className="rounded-2xl border border-border/60 bg-muted/30 p-3" data-testid={`cleanup-preview-${col}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-sm capitalize">{col.replace(/_/g, " ")}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-bold">{n} record{n === 1 ? "" : "s"}</span>
                  </div>
                  {(preview.samples?.[col] || []).slice(0, 3).map((s, i) => (
                    <p key={i} className="text-[11px] text-muted-foreground italic truncate font-mono">
                      • {Object.values(s).filter(Boolean).join(" · ").slice(0, 110)}
                    </p>
                  ))}
                  {n > (preview.samples?.[col] || []).length && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">…and {n - (preview.samples?.[col] || []).length} more</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            {preview?.total > 0 && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirm(); }}
                disabled={confirming}
                className="rounded-2xl bg-red-600 hover:bg-red-700"
                data-testid="cleanup-data-confirm"
              >
                <Trash2 className="h-4 w-4 mr-1" /> {confirming ? "Deleting…" : `Delete ${preview.total}`}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [domains, setDomains] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setS(r.data);
      setDomains((r.data.allowed_login_domains || []).join(", "));
      setLoading(false);
    }).catch((err) => { toast.error(fmtError(err)); setLoading(false); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        session_idle_timeout_min: Number(s.session_idle_timeout_min),
        allowed_login_domains: domains.split(",").map((x) => x.trim()).filter(Boolean),
        require_strong_password: !!s.require_strong_password,
        archive_on_delete: !!s.archive_on_delete,
        two_factor_required: !!s.two_factor_required,
      };
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Security settings saved");
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  if (loading || !s) return <div className="p-6"><Skeleton className="h-96 rounded-3xl" /></div>;

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[900px] mx-auto space-y-5">
      <header className="mb-6 flex items-start gap-3">
        <div className="h-12 w-12 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Security & access.</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Tune how your team logs in and how long sessions last. Risky defaults are conservative.</p>
        </div>
      </header>

      <Card className="rounded-3xl border-border/60 shadow-soft mb-5">
        <CardContent className="p-6 space-y-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Session</p>
            <Label className="text-xs font-semibold">Idle timeout (minutes)</Label>
            <Input
              type="number"
              min={5}
              max={1440}
              value={s.session_idle_timeout_min}
              onChange={(e) => setS({ ...s, session_idle_timeout_min: e.target.value })}
              className="rounded-2xl h-11 mt-1.5 max-w-xs"
              data-testid="settings-idle-input"
            />
            <p className="text-xs text-muted-foreground mt-1.5">User auto-logged out after N minutes of no activity (5 – 1440).</p>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Allowed login domains</p>
            <Input
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder="renviro.com, partner.com"
              className="rounded-2xl h-11"
              data-testid="settings-domains-input"
            />
            <p className="text-xs text-muted-foreground mt-1.5">Comma-separated. Leave blank to allow any approved email. Affects both password & Google login.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 rounded-2xl border border-border/60">
              <div>
                <p className="font-semibold text-sm">Strong password required</p>
                <p className="text-xs text-muted-foreground mt-0.5">10+ chars, mixed case, digit, symbol.</p>
              </div>
              <Switch checked={!!s.require_strong_password} onCheckedChange={(v) => setS({ ...s, require_strong_password: v })} data-testid="settings-strong-password" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-2xl border border-border/60">
              <div>
                <p className="font-semibold text-sm">Archive instead of delete</p>
                <p className="text-xs text-muted-foreground mt-0.5">Soft-delete records; admin only can hard-delete.</p>
              </div>
              <Switch checked={!!s.archive_on_delete} onCheckedChange={(v) => setS({ ...s, archive_on_delete: v })} data-testid="settings-archive" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-2xl border border-border/60 sm:col-span-2 opacity-70">
              <div>
                <p className="font-semibold text-sm">Two-factor authentication</p>
                <p className="text-xs text-muted-foreground mt-0.5">Coming soon — OTP via email/authenticator. Toggle saves intent.</p>
              </div>
              <Switch checked={!!s.two_factor_required} onCheckedChange={(v) => setS({ ...s, two_factor_required: v })} data-testid="settings-2fa" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} className="rounded-2xl tap" data-testid="settings-save-button">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-2xl bg-primary/10 text-primary grid place-items-center shrink-0">
              <Database className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">Backups</p>
              <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Snapshot & restore</p>
              <p className="text-sm text-muted-foreground mt-1">
                Auto-snapshots run nightly at 02:00 UTC and the last 30 days are kept on disk. Download a fresh backup any time and save it off-site (Google Drive / iCloud) for full safety.
              </p>
            </div>
          </div>
          <BackupAction />
        </CardContent>
      </Card>

      <DriveBackupCard />

      <Card className="rounded-3xl border-amber-200/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-2xl bg-amber-50 text-amber-700 grid place-items-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">Maintenance</p>
              <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Cleanup test data</p>
              <p className="text-sm text-muted-foreground mt-1">
                Permanently delete records whose name / title / email matches a test pattern
                (<code className="text-xs">TEST_</code>, <code className="text-xs">iter_</code>, <code className="text-xs">demo_</code>, <code className="text-xs">sample_</code>, <code className="text-xs">QA_</code>, <code className="text-xs">playwright</code>, <code className="text-xs">automation</code>).
                <strong className="text-foreground"> Real customer data is never touched</strong> — to remove a real client/deal/lead, archive it via its detail page instead.
              </p>
            </div>
          </div>
          <CleanupTestDataAction />
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
              <Wrench className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Catalog cleanup</p>
              <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Move services out of Products</p>
              <p className="text-sm text-muted-foreground mt-1">
                Re-classifies any product whose category contains
                <code className="text-xs mx-1">Training</code>·<code className="text-xs mx-1">Calibration</code>·<code className="text-xs mx-1">Rental</code>·<code className="text-xs mx-1">Repair</code>
                into the <strong>Services</strong> tab. Existing prices, UOM, supplier and SKU are preserved. New rows added under those categories are auto-routed automatically — this button is for legacy data only.
              </p>
            </div>
          </div>
          <MigrateServicesAction />
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-red-200/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-2xl bg-red-50 text-red-700 grid place-items-center shrink-0">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Danger zone</p>
              <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Reset Performance Hub</p>
              <p className="text-sm text-muted-foreground mt-1">
                Clears every point, milestone, streak and notification so all reps restart from <strong>zero</strong>. Business data (deals, leads, clients, quotations, activities) stays untouched. Re-usable — press it anytime you want a clean season reset. Type <code className="text-xs">RESET</code> to confirm.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ResetPerfHubAction />
            <CleanupPhantomEventsAction />
            <RecomputeAllPointsAction />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-red-200/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-2xl bg-red-50 text-red-700 grid place-items-center shrink-0">
              <Trash2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Danger zone</p>
              <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Wipe customer contacts</p>
              <p className="text-sm text-muted-foreground mt-1">
                Removes every customer contact / decision-maker so each rep can add fresh ones from scratch. <strong>Clients, Leads, Marketing Contacts, Deals and Quotations stay intact.</strong> Re-usable — press it anytime you want a clean reset. Type <code className="text-xs">WIPE</code> to confirm.
              </p>
            </div>
          </div>
          <WipeContactsAction />
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-red-200/60 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-2xl bg-red-50 text-red-700 grid place-items-center shrink-0">
              <Trash2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Danger zone</p>
              <p className="font-display text-lg font-extrabold tracking-tight mt-0.5">Wipe ALL CRM data</p>
              <p className="text-sm text-muted-foreground mt-1">
                Use this when the safer "Cleanup test data" can't catch everything (e.g. real-looking dummy entries like <code className="text-xs">"Initial call"</code> or <code className="text-xs">"Send proposal"</code>). Permanently deletes every client, deal, follow-up, lead, quotation, contact, activity, meeting and notification — but keeps users, products, services and audit logs.
                <strong className="text-red-700"> This cannot be undone.</strong> Type <code className="text-xs">WIPE</code> to confirm.
              </p>
            </div>
          </div>
          <WipeAllDataAction />
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Security principle</p>
          <p className="font-display text-lg font-extrabold tracking-tight">Make risky actions secure — not daily actions difficult.</p>
          <p className="text-sm text-muted-foreground mt-1">Bulk import, exports, deletes, ownership reassignment and permission changes are logged. Admins can review or revert via Audit Logs.</p>
        </CardContent>
      </Card>
    </div>
  );
}
